import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS, GPS_RADIUS_METRES } from '../../lib/workerData';
import { formatTime } from '../../lib/utils';
import { isGpsMismatch, isGpsUnknown } from './TonightView';

const LOOKBACK_HOURS = 18;

const CLEARANCE_ITEMS = [
  { key: 'conduct_check', label: 'Conduct Check' },
  { key: 'vulnerable_sector', label: 'Vulnerable Sector' },
  { key: 'whmis', label: 'WHMIS' },
  { key: 'secure_access', label: 'Secure Access' },
];

function buildingById(id) { return BUILDINGS.find(b => b.id === id) || null; }

function elapsed(iso) {
  if (!iso) return '—';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  const h = Math.floor(mins / 60), m = mins % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

/**
 * One shared worker card, used identically no matter what put it on screen —
 * a map pin click, a status-board row click, or an exceptions-queue item
 * click all just pass a workerId in here (plus that worker's today-expected
 * building list, computed once by CommandCentral via TonightView's own
 * buildExpectedMap, so this never re-derives "who's expected where" on its
 * own and risks disagreeing with the rest of the view).
 */
export default function WorkerDetail({ workerId, expectedBuildings = [], onClose }) {
  const [profile, setProfile] = useState(null);
  const [todaySubs, setTodaySubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    const since = new Date(Date.now() - LOOKBACK_HOURS * 3600000).toISOString();
    const [profRes, subsRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', workerId).maybeSingle(),
      supabase.from('checklist_submissions').select('*').eq('worker_id', workerId).gte('sign_in', since).order('sign_in', { ascending: false }),
    ]);
    setProfile(profRes.data || null);
    setTodaySubs(subsRes.data || []);
    setLoading(false);
  }, [workerId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!workerId) return null;

  const openSub = todaySubs.find(r => !r.sign_out);
  const latestSub = todaySubs[0] || null;
  const activeSub = openSub || latestSub;

  let gpsState = 'none';
  if (activeSub) {
    if (isGpsMismatch(activeSub)) gpsState = 'mismatch';
    else if (isGpsUnknown(activeSub)) gpsState = 'unknown';
    else if (activeSub.gps_verified) gpsState = 'verified';
  }
  const gpsMeta = {
    verified: { color: '#2F7A4F', bg: 'rgba(47,122,79,0.10)', label: 'GPS Verified' },
    mismatch: { color: '#B33A2E', bg: 'rgba(179,58,46,0.10)', label: 'GPS Mismatch — Off-Site' },
    unknown: { color: '#96600A', bg: 'rgba(181,115,10,0.10)', label: 'No GPS Reading' },
    none: { color: '#5B6B72', bg: 'rgba(91,107,114,0.10)', label: 'Not Checked In' },
  }[gpsState];

  const building = activeSub ? buildingById(activeSub.building_id) : (expectedBuildings[0] ? buildingById(expectedBuildings[0]) : null);

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.panel} onClick={e => e.stopPropagation()}>
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>

        {loading && !profile ? (
          <div style={styles.loading}>Loading worker…</div>
        ) : !profile ? (
          <div style={styles.loading}>Worker not found.</div>
        ) : (
          <>
            <div style={styles.headerRow}>
              <div style={{ ...styles.avatar, background: gpsMeta.color }}>{profile.full_name?.[0] || '?'}</div>
              <div style={{ minWidth: 0 }}>
                <div style={styles.name}>{profile.full_name}</div>
                <div style={styles.roleLine}>{(profile.role || 'worker').replace('_', ' ')} · {(profile.employment_type || '').replace('_', ' ')}</div>
              </div>
            </div>

            <div style={{ ...styles.statusBanner, color: gpsMeta.color, background: gpsMeta.bg, borderColor: gpsMeta.color }}>
              {gpsMeta.label}
              {activeSub?.gps_distance_metres != null && gpsState === 'mismatch' && (
                <span style={styles.statusSub}> · about {activeSub.gps_distance_metres}m from building (radius {GPS_RADIUS_METRES}m)</span>
              )}
              {activeSub?.gps_accuracy != null && gpsState !== 'none' && (
                <span style={styles.statusSub}> · ±{activeSub.gps_accuracy}m accuracy</span>
              )}
            </div>

            <div style={styles.grid}>
              <Field label="Building right now">{building ? (building.shortName || building.name) : '—'}</Field>
              <Field label="Check-in">{activeSub?.sign_in ? `${formatTime(activeSub.sign_in)} (${elapsed(activeSub.sign_in)} ago)` : '—'}</Field>
              <Field label="Check-out">{activeSub ? (activeSub.sign_out ? formatTime(activeSub.sign_out) : 'Still on site') : '—'}</Field>
              <Field label="Coordinates">
                {activeSub?.gps_lat != null ? `${activeSub.gps_lat.toFixed(5)}, ${activeSub.gps_lng.toFixed(5)}` : (activeSub?.gps_error || 'No reading')}
              </Field>
            </div>

            {building && (
              <div style={styles.buildingBox}>
                <div style={styles.buildingBoxName}>{building.name}</div>
                <div style={styles.buildingBoxAddr}>{building.address}</div>
                {building.window && <div style={styles.buildingBoxWindow}>{building.window}</div>}
                {building.note && <div style={styles.buildingBoxNote}>{building.note}</div>}
              </div>
            )}

            <div style={styles.sectionLabel}>Compliance</div>
            <div style={styles.clearanceRow}>
              {CLEARANCE_ITEMS.map(item => (
                <div key={item.key} style={{ ...styles.clearanceChip, borderColor: profile[item.key] ? '#2F7A4F' : '#B33A2E', color: profile[item.key] ? '#2F7A4F' : '#B33A2E' }}>
                  {profile[item.key] ? '✓' : '✕'} {item.label}
                </div>
              ))}
            </div>

            <div style={styles.sectionLabel}>Today's Assignments</div>
            {expectedBuildings.length === 0 ? (
              <div style={styles.emptyNote}>Not on the schedule today.</div>
            ) : (
              <div style={styles.assignList}>
                {expectedBuildings.map(bid => {
                  const b = buildingById(bid);
                  const sub = todaySubs.find(r => r.building_id === bid);
                  return (
                    <div key={bid} style={styles.assignRow}>
                      <span style={styles.assignBuilding}>{b?.shortName || b?.name || bid}</span>
                      <span style={styles.assignStatus}>
                        {sub ? (sub.sign_out ? `Done · ${formatTime(sub.sign_in)}–${formatTime(sub.sign_out)}` : `On site · in ${formatTime(sub.sign_in)}`) : 'Not checked in yet'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {profile.notes && (
              <>
                <div style={styles.sectionLabel}>Notes</div>
                <div style={styles.emptyNote}>{profile.notes}</div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={styles.field}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={styles.fieldValue}>{children}</div>
    </div>
  );
}

const styles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(26,43,51,0.45)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' },
  panel: {
    width: 420, maxWidth: '92vw', height: '100%', background: '#FFFFFF', borderLeft: '1px solid #E4E0D8',
    padding: '28px 26px 32px', overflowY: 'auto', position: 'relative', boxShadow: '-12px 0 32px rgba(26,43,51,0.15)',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  closeBtn: { position: 'absolute', top: 18, right: 18, background: 'transparent', border: 'none', color: '#5B6B72', fontSize: 26, lineHeight: 1, cursor: 'pointer' },
  loading: { color: '#5B6B72', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  headerRow: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 },
  avatar: { width: 48, height: 48, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontWeight: 800, fontSize: 20, flexShrink: 0 },
  name: { fontSize: 21, fontWeight: 700, color: '#1A2B33', letterSpacing: 0.2 },
  roleLine: { fontSize: 12, color: '#5B6B72', textTransform: 'capitalize', marginTop: 2 },
  statusBanner: { border: '1px solid', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 700, marginBottom: 18 },
  statusSub: { fontWeight: 500, opacity: 0.85 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', marginBottom: 18 },
  field: {},
  fieldLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: '#5B6B72', textTransform: 'uppercase', marginBottom: 3 },
  fieldValue: { fontSize: 13, color: '#1A2B33', fontWeight: 500 },
  buildingBox: { background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 10, padding: '12px 14px', marginBottom: 18 },
  buildingBoxName: { fontSize: 14, fontWeight: 700, color: '#1A2B33' },
  buildingBoxAddr: { fontSize: 12, color: '#5B6B72', marginTop: 2 },
  buildingBoxWindow: { fontSize: 12, color: '#5B6B72', marginTop: 6 },
  buildingBoxNote: { fontSize: 12, color: '#96600A', marginTop: 6, lineHeight: 1.4 },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#5B6B72', textTransform: 'uppercase', marginBottom: 8, marginTop: 4 },
  clearanceRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 },
  clearanceChip: { border: '1px solid', borderRadius: 999, padding: '4px 10px', fontSize: 11.5, fontWeight: 700 },
  assignList: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 },
  assignRow: { display: 'flex', justifyContent: 'space-between', gap: 10, background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '8px 12px', fontSize: 12.5 },
  assignBuilding: { color: '#1A2B33', fontWeight: 600 },
  assignStatus: { color: '#5B6B72' },
  emptyNote: { fontSize: 12.5, color: '#5B6B72', lineHeight: 1.5, marginBottom: 12 },
};
